// /login?token=… — the link from the bot. Preview first ("Is this you?") so the single-use token
// is only spent when the person confirms; then welcome and the add-to-home-screen nudge.
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError, setSession } from '../api';
import { Link, navigate } from '../router';
import { BrokenLink, Chat, Lock, Phone } from '../icons';
import { canInstall, install, isIOS, isStandalone } from '../install';
import type { Profile, SavedReel } from '../types';
import { applyLang, useT } from '../i18n';
import { bold } from '../lib/rich';
import { save, KEYS } from '../storage';

const DM_URL = 'https://ig.me/m/eat.recipe';
type Step = 'checking' | 'confirm' | 'signing' | 'welcome' | 'expired' | 'error';

export function Login() {
  const t = useT();
  const [token] = useState(() => new URLSearchParams(location.search).get('token') || '');
  const [step, setStep] = useState<Step>(token ? 'checking' : 'expired');
  const [who, setWho] = useState<Profile | null>(null);
  const [reels, setReels] = useState<SavedReel[]>([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    // Keep the token out of history, screenshots and any later referrer.
    history.replaceState(null, '', '/login');
    if (!token) return;
    api.preview(token)
      .then((p) => { setWho(p); setStep('confirm'); })
      .catch((e: ApiError) => {
        if (e.status === 401 || e.status === 400) setStep('expired');
        else { setErr(e.message); setStep('error'); }
      });
  }, []);

  const go = async () => {
    setStep('signing');
    const started = Date.now();
    try {
      const s = await api.exchange(token);
      setSession(s.session, s.expires_at);
      // If the bot already asked the language, use it from the first screen on.
      const me = await api.me().catch(() => null);
      if (me) {
        save(KEYS.me, me);
        if (me.language) applyLang(me.language);
      }
      const list = await api.reels().catch(() => [] as SavedReel[]);
      setReels(list);
      await new Promise((r) => setTimeout(r, Math.max(0, 900 - (Date.now() - started))));
      setStep('welcome');
    } catch (e) {
      const ae = e as ApiError;
      if (ae.status === 401) setStep('expired');
      else { setErr(ae.message); setStep('error'); }
    }
  };

  const handle = who?.username ? `@${who.username}` : t('auth.yourAccount');
  const firstRaw = (who?.name || who?.username || '').split(/\s+/)[0];
  const first = firstRaw ? firstRaw[0].toUpperCase() + firstRaw.slice(1) : '';

  return (
    <div class="page" style="padding:24px 20px 28px">
      <div class="wordmark">eat<span>.</span>recipe</div>

      {step === 'checking' && (
        <div style="flex:1;display:flex;align-items:center;justify-content:center" aria-busy="true">
          <div class="muted">{t('auth.checking')}</div>
        </div>
      )}

      {step === 'confirm' && (
        <div style="flex:1;display:flex;flex-direction:column">
          <div style="margin-top:56px;display:flex;flex-direction:column;align-items:center;gap:14px;text-align:center">
            <span aria-hidden="true" style="width:96px;height:96px;border-radius:999px;background:var(--accent);color:var(--on-accent);display:flex;align-items:center;justify-content:center;font-family:var(--display);font-weight:800;font-size:40px">{who?.initial || '•'}</span>
            <div style="display:flex;flex-direction:column;gap:4px">
              <h1 class="h1" style="font-size:28px;line-height:1.15">{t('auth.isYou')}</h1>
              <div style="font-size:18px;font-weight:700">{handle}</div>
            </div>
          </div>
          <div class="card" style="margin-top:28px;padding:14px 16px;border-radius:16px;display:flex;gap:12px;align-items:flex-start">
            <Lock style="flex:none;margin-top:1px" />
            <div class="muted" style="font-size:14px;line-height:1.55">{bold(t('auth.linkFrom'), { h: '@eat.recipe' })}</div>
          </div>
          <div style="margin-top:auto;padding-top:28px;display:flex;flex-direction:column;gap:12px">
            <button class="btn btn-primary btn-lg" onClick={go}>{t('auth.continueAs', { who: handle })}</button>
            <Link href="/signin" style="min-height:44px;display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:600">{t('auth.notYou')}</Link>
          </div>
        </div>
      )}

      {step === 'signing' && (
        <div style="flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;text-align:center" role="status">
          <div class="display" style="font-weight:700;font-size:24px">{t('auth.opening')}</div>
          <div style="width:180px;height:6px;border-radius:3px;background:var(--soft);overflow:hidden">
            <div style="width:40%;height:6px;border-radius:3px;background:var(--accent-text);animation:er-slide 1s ease-in-out infinite" />
          </div>
        </div>
      )}

      {step === 'welcome' && <Welcome name={first} reels={reels} />}

      {step === 'expired' && (
        <div style="flex:1;display:flex;flex-direction:column">
          <div style="margin-top:56px;display:flex;flex-direction:column;gap:10px">
            <span style="width:64px;height:64px;border-radius:20px;background:var(--soft);display:flex;align-items:center;justify-content:center"><BrokenLink /></span>
            <h1 class="h1" style="font-size:30px;line-height:1.12">{t('auth.used')}</h1>
            <div style="font-size:16px;line-height:1.6">{t('auth.usedBody')}</div>
          </div>
          <div class="card" style="margin-top:24px;padding:14px 16px;border-radius:16px;display:flex;flex-direction:column;gap:10px">
            {[bold(t('auth.stepOpen'), { h: '@eat.recipe' }), bold(t('auth.stepSend'), { w: 'login' })].map((line, i) => (
              <div style="display:flex;gap:10px;align-items:center">
                <span style="flex:none;width:28px;height:28px;border-radius:999px;background:var(--accent);color:var(--on-accent);display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px">{i + 1}</span>
                <span style="font-size:16px">{line}</span>
              </div>
            ))}
          </div>
          <div style="margin-top:auto;padding-top:24px;display:flex;flex-direction:column;gap:10px">
            <a class="btn btn-primary btn-lg" href={DM_URL}><Chat />{t('auth.newLink')}</a>
            <div class="muted" style="font-size:14px;text-align:center">{t('auth.safe')}</div>
          </div>
        </div>
      )}

      {step === 'error' && (
        <div style="flex:1;display:flex;flex-direction:column;justify-content:center;gap:14px" role="alert">
          <h1 class="h1" style="font-size:26px">{t('auth.checkFailed')}</h1>
          <div class="muted">{err || t('auth.checkConn')}</div>
          <button class="btn btn-primary" onClick={() => location.reload()}>{t('common.tryAgain')}</button>
        </div>
      )}
    </div>
  );
}

function Welcome({ name, reels }: { name: string; reels: SavedReel[] }) {
  const t = useT();
  const ready = reels.filter((r) => r.status === 'ready').length;
  const reading = reels.filter((r) => r.status === 'queued' || r.status === 'processing').length;
  const [installable, setInstallable] = useState(canInstall());
  useEffect(() => {
    const id = setInterval(() => setInstallable(canInstall()), 500);
    return () => clearInterval(id);
  }, []);
  const showInstall = !isStandalone() && (installable || isIOS());
  const summary = reels.length === 0
    ? t('auth.summaryEmpty')
    : [t('auth.summary', { n: ready }), reading ? t('auth.reading', { n: reading }) : ''].filter(Boolean).join(' ');
  return (
    <div style="flex:1;display:flex;flex-direction:column">
      <div style="margin-top:48px;display:flex;flex-direction:column;gap:6px">
        <div class="display" style="font-weight:800;font-size:40px;line-height:1.3;color:var(--accent-text)">{t('auth.hello')}</div>
        <h1 class="h1" style="font-size:36px;line-height:1.05">{name || t('auth.friend')}</h1>
        <div class="muted" style="margin-top:8px;font-size:16px;line-height:1.55">{summary}</div>
      </div>
      {showInstall && (
        <div style="margin-top:28px;padding:16px;border-radius:18px;background:var(--soft);display:flex;flex-direction:column;gap:12px">
          <div style="display:flex;gap:12px;align-items:flex-start">
            <span style="flex:none;width:44px;height:44px;border-radius:12px;background:var(--card);display:flex;align-items:center;justify-content:center"><Phone /></span>
            <div style="display:flex;flex-direction:column;gap:2px">
              <div style="font-size:16px;font-weight:700">{t('settings.install')}</div>
              <div class="muted" style="font-size:14px;line-height:1.5">
                {installable ? t('auth.installBody') : t('settings.installIOS')}
              </div>
            </div>
          </div>
          {installable && <button class="btn btn-outline" style="min-height:48px;border-radius:12px;font-size:15px" onClick={() => install().then(() => setInstallable(canInstall()))}>{t('settings.installBtn')}</button>}
        </div>
      )}
      <div style="margin-top:auto;padding-top:28px">
        <button class="btn btn-primary btn-lg" style="width:100%" onClick={() => navigate('/', { replace: true })}>{t('auth.openLibrary')}</button>
      </div>
    </div>
  );
}
